<template>
  <AiEducationLayout>
    <EducationPageHeader title="区域管理工作台" description="查看当前账号获授权的区域学校与学情汇总" />
    <ContentWrap>
      <EducationState v-if="status === 'loading'" state="loading" title="正在加载区域管理工作台" />
      <EducationState
        v-else-if="status === 'error'"
        state="error"
        title="区域管理工作台加载失败"
        description="请确认当前账号已配置有效的区域管理关系。"
        @retry="load"
      />
      <EducationState
        v-else-if="!schoolIds.length"
        state="empty"
        title="暂无授权学校"
        description="请联系平台管理员配置区域学校范围。"
      />
      <template v-else>
        <el-alert
          title="本页数据按当前账号区域授权学校过滤，与平台全量概览口径不同属正常。"
          type="info"
          :closable="false"
          class="mb-12px"
        />
        <el-row v-if="summaryCards.length" :gutter="16" class="summary-grid">
          <el-col v-for="item in summaryCards" :key="item.label" :xs="12" :sm="8" :lg="4">
            <div class="summary-card">
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
            </div>
          </el-col>
        </el-row>
        <el-row :gutter="16" class="school-grid">
          <el-col v-for="school in authorizedSchools" :key="school.id" :xs="24" :sm="12" :lg="8">
            <el-card shadow="never" class="school-card">
              <span>授权学校</span>
              <strong>{{ school.name }}</strong>
              <small>{{ school.eduStageLabel || '可访问范围由后端校验' }}</small>
            </el-card>
          </el-col>
        </el-row>
        <div class="quick-links">
          <el-button type="primary" @click="router.push(`${BASE}/analytics`)">学情分析</el-button>
          <el-button @click="router.push(`${BASE}/overview`)">返回概览</el-button>
        </div>
      </template>
    </ContentWrap>
  </AiEducationLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import AiEducationLayout from '../layout/AiEducationLayout.vue'
import EducationPageHeader from '../components/EducationPageHeader.vue'
import EducationState from '../components/EducationState.vue'
import { useEducationWorkspace } from '../composables/useEducationWorkspace'
import { AI_EDUCATION_BASE as BASE } from '../composables/useAdminEducationMenu'
import {
  getPrincipalAnalyticsSummary,
  getSchoolOptions,
  type StudentSummaryVO,
  type SchoolVO
} from '@/api/aiEducation'
import { DICT_TYPE, getDictLabel } from '@/utils/dict'

defineOptions({ name: 'AiEducationRegionAdminHome' })

const router = useRouter()
const { workspace, initialize } = useEducationWorkspace()
const status = ref<'loading' | 'ready' | 'error'>('loading')
const summary = ref<StudentSummaryVO>()
const schoolMap = ref<Record<number, SchoolVO>>({})
const schoolIds = computed(() => workspace.value?.dataScope?.schoolIds || [])

const authorizedSchools = computed(() => {
  const orgs = workspace.value?.organizations || []
  return schoolIds.value.map((id) => {
    const fromOrg = orgs.find((o) => o.organizationId === id)
    const fromApi = schoolMap.value[id]
    const eduStage = fromApi?.eduStage || (fromOrg as any)?.eduStage
    return {
      id,
      name:
        fromApi?.name ||
        fromOrg?.organizationName ||
        (id != null ? `学校（编号 ${id}）` : '未命名学校'),
      eduStageLabel: eduStage ? getDictLabel(DICT_TYPE.AI_EDU_STAGE, eduStage) || eduStage : ''
    }
  })
})

const summaryCards = computed(() => {
  const s = summary.value
  if (!s) return []
  return [
    { label: '完成章节', value: s.chaptersCompleted ?? 0 },
    { label: '答对题数', value: s.correctAnswers ?? 0 },
    { label: '答错题数', value: s.wrongAnswers ?? 0 },
    { label: '提交作业', value: s.assignmentsSubmitted ?? 0 }
  ]
})

const load = async () => {
  status.value = 'loading'
  try {
    await initialize()
    const [sum, schools] = await Promise.all([
      getPrincipalAnalyticsSummary().catch(() => undefined),
      getSchoolOptions().catch(() => [] as SchoolVO[])
    ])
    summary.value = sum
    schoolMap.value = Object.fromEntries((schools || []).map((s) => [s.id!, s]))
    status.value = 'ready'
  } catch {
    status.value = 'error'
  }
}

onMounted(load)
</script>

<style scoped>
.summary-grid,
.school-grid {
  margin-bottom: 16px;
}
.summary-card,
.school-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  margin-bottom: 12px;
  border-radius: 12px;
  background: #f8fafc;
}
.summary-card span,
.school-card span,
.school-card small {
  color: #64748b;
  font-size: 13px;
}
.summary-card strong,
.school-card strong {
  color: #0f172a;
  font-size: 22px;
}
.quick-links {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
</style>
